import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useCallback } from 'react';

/**
 * Reusable hook for fetching paginated list data with infinite scroll
 * 
 * @param {Object} config
 * @param {string|Array} config.queryKey - Base query key for React Query
 * @param {Function} config.queryFn - Function to fetch data: ({ pageParam }) => Promise
 * @param {Function} config.buildParams - Function to build query params: (filters, pageParam) => Object
 * @param {Function} config.transformResponse - Function to transform API response: (response) => Array
 * @param {Function} config.clientSideFilter - Optional client-side filtering: (items, filters) => Array
 * @param {Function} config.clientSideSort - Optional client-side sorting: (items, sortBy) => Array
 * @param {Object} config.filters - Filter state object (searchTerm, selectedTags, etc.)
 * @param {number} config.limit - Items per page (default: 20)
 * @param {number} config.maxPages - Maximum pages to fetch (default: 10)
 * @param {number} config.staleTime - React Query staleTime in ms (default: 5 minutes)
 * @param {Object} config.queryOptions - Additional React Query options
 * @returns {Object} { projects, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, refetch, error }
 */
export const useListPageData = ({
  queryKey,
  queryFn,
  buildParams,
  transformResponse,
  clientSideFilter,
  clientSideSort,
  filters = {},
  limit = 20,
  maxPages = 10,
  staleTime = 5 * 60 * 1000,
  queryOptions = {},
}) => {
  const queryClient = useQueryClient();

  // Build query key with all relevant filter values
  const fullQueryKey = useMemo(() => {
    const baseKey = Array.isArray(queryKey) ? queryKey : [queryKey];
    // Include all filter values that affect the query
    // Sort entries by key for stable query keys
    const filterEntries = Object.entries(filters)
      .filter(([_, value]) => {
        // Filter out null, undefined, empty string, and false boolean values
        // But keep objects (even empty ones) and numbers (including 0)
        if (value === null || value === undefined || value === '') return false;
        // Keep boolean false for mapSyncEnabled and mapUserInteracted as they affect filtering
        if (typeof value === 'boolean') return true;
        // Keep objects (they might be empty but still meaningful)
        if (typeof value === 'object') return true;
        return true;
      })
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
      .map(([key, value]) => {
        // Include key name to avoid collisions between different filters with same value
        // Handle arrays and objects
        if (Array.isArray(value)) {
          return `${key}:${value.sort().join(',')}`;
        }
        if (typeof value === 'object') {
          try {
            return `${key}:${JSON.stringify(value)}`;
          } catch {
            // Fallback if object can't be stringified (shouldn't happen with our data)
            return `${key}:${String(value)}`;
          }
        }
        return `${key}:${value}`;
      });
    const key = [...baseKey, ...filterEntries];
    
    // Debug log for ProjectsPage
    if (baseKey[0] === 'user-projects') {
      console.log('[useListPageData] Query key updated', {
        mapViewportCenter: filters.mapViewportCenter,
        mapUserInteracted: filters.mapUserInteracted,
        mapSyncEnabled: filters.mapSyncEnabled,
        fullKey: key,
      });
    }
    
    return key;
  }, [queryKey, filters]);

  // Build query function - use buildParams if provided, otherwise use queryFn directly
  const effectiveQueryFn = useCallback(
    ({ pageParam = 0 }) => {
      if (buildParams) {
        // If buildParams is provided, use it to build params and call queryFn with the result
        const params = buildParams(filters, pageParam, limit);
        // Debug log for ProjectsPage
        if (Array.isArray(queryKey) ? queryKey[0] === 'user-projects' : queryKey === 'user-projects') {
          console.log('[useListPageData] Calling queryFn with params', params);
        }
        const result = queryFn(params);
        // Debug log for ProjectsPage
        if (Array.isArray(queryKey) ? queryKey[0] === 'user-projects' : queryKey === 'user-projects') {
          console.log('[useListPageData] QueryFn called, result:', result);
        }
        return result;
      } else {
        // Otherwise, queryFn should handle everything including pageParam
        return queryFn({ pageParam });
      }
    },
    [buildParams, queryFn, filters, limit, queryKey]
  );

  // Fetch data with infinite query
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: fullQueryKey,
    queryFn: effectiveQueryFn,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const items = transformResponse(lastPage);
      
      // Stop if empty page
      if (!items || items.length === 0) return undefined;
      
      // Stop if fewer items than limit (reached end)
      if (items.length < limit) return undefined;
      
      // Stop if max pages reached
      if (allPages.length >= maxPages) return undefined;
      
      // Check for pagination metadata if available
      const pagination = lastPage?.data?.data?.pagination || lastPage?.data?.pagination;
      if (pagination && pagination.hasMore === false) return undefined;
      
      // Calculate next offset based on total items fetched
      const totalFetched = allPages.reduce((sum, page) => {
        const pageItems = transformResponse(page);
        return sum + (pageItems?.length || 0);
      }, 0);
      
      return totalFetched;
    },
    staleTime,
    // Ensure we don't use stale data when query key changes
    // This is important for location-based filtering where we want fresh results
    placeholderData: (previousData) => {
      // Don't show placeholder data when query key changes significantly
      // (e.g., when location filters change)
      return undefined;
    },
    ...queryOptions,
  });

  // Flatten and transform all pages
  const allItems = useMemo(() => {
    if (!data?.pages) return [];
    
    const flattened = data.pages.reduce((acc, page) => {
      const items = transformResponse(page);
      return [...acc, ...(items || [])];
    }, []);
    
    // Deduplicate by ID if items have id property
    const seen = new Set();
    const deduplicated = flattened.filter((item) => {
      if (!item?.id) return true; // Keep items without id
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
    
    // Debug log for ProjectsPage
    if (Array.isArray(queryKey) ? queryKey[0] === 'user-projects' : queryKey === 'user-projects') {
      console.log('[useListPageData] Processed items', {
        totalPages: data.pages.length,
        totalItems: deduplicated.length,
        hasLocationFilter: filters.mapViewportCenter && filters.mapUserInteracted && filters.mapSyncEnabled,
        sampleItem: deduplicated[0],
      });
    }
    
    return deduplicated;
  }, [data, transformResponse, queryKey, filters]);

  // Apply client-side filtering if provided
  const filteredItems = useMemo(() => {
    if (!clientSideFilter) return allItems;
    return clientSideFilter(allItems, filters);
  }, [allItems, filters, clientSideFilter]);

  // Apply client-side sorting if provided
  const sortedItems = useMemo(() => {
    if (!clientSideSort) return filteredItems;
    return clientSideSort(filteredItems, filters.sortBy || filters.sort);
  }, [filteredItems, filters, clientSideSort]);

  // Refetch function that also invalidates related queries
  const refetchWithInvalidation = useCallback(
    async (invalidateKeys = []) => {
      // Invalidate specified query keys
      await Promise.all(
        invalidateKeys.map((key) =>
          queryClient.invalidateQueries({ queryKey: Array.isArray(key) ? key : [key] })
        )
      );
      // Refetch current query
      return refetch();
    },
    [queryClient, refetch]
  );

  return {
    projects: sortedItems,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchWithInvalidation,
    error,
  };
};

